/**
 * TabBar：底部 Tab 导航
 * - App：排盘 / 学习 / AI 对话 / 我的
 * - Web：排盘 / 我的 / 下载App（学习、AI 对话、会员为 App 独占；web 提供匿名排盘 + 注册/登录云同步 + 下载引导）
 * - fixed 底部 + safe-area 适配手势条
 * - hash 驱动高亮：子路由（cases/case-form 归排盘）映射所属 Tab
 */
import type { Page } from '../App';

export const APP_TABS = [
  { key: 'paipan', label: '排盘' },
  { key: 'learn', label: '学习' },
  { key: 'chat', label: 'AI 对话' },
  { key: 'mine', label: '我的' },
] as const;

export const WEB_TABS = [
  { key: 'paipan', label: '排盘' },
  { key: 'mine', label: '我的' },
  { key: 'download', label: '下载App' },
] as const;

export type TabKey = (typeof APP_TABS)[number]['key'] | 'download';

/** 页面 → 所属 Tab */
export function tabOfPage(page: Page): TabKey {
  if (page === 'download') return 'download';
  if (page === 'learn') return 'learn';
  if (page === 'chat') return 'chat';
  if (page === 'mine' || page === 'membership' || page === 'register' || page === 'reset') return 'mine';
  return 'paipan'; // paipan / cases / case-form
}

/** Tab 首页 hash */
export function tabHome(tab: TabKey): string {
  return `#/${tab === 'paipan' ? 'paipan' : tab}`;
}

/**
 * 单色线性 Tab 图标：全部 stroke=currentColor / fill=none，
 * 颜色随选中态（text-slate-800 / text-slate-400）自动跟随，避免 emoji 系统色（如未选中显蓝）。
 * 排盘=紫微命盘宫格（十二宫环抱中宫），学习=书，我的=人像，下载=下载箭头。
 */
function TabIcon({ name }: { name: TabKey }) {
  const p = {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
    case 'paipan':
      return (
        <svg {...p}>
          <rect x="3" y="3" width="18" height="18" rx="2.5" />
          <rect x="9" y="9" width="6" height="6" rx="1" />
          <line x1="9" y1="3" x2="9" y2="9" />
          <line x1="15" y1="3" x2="15" y2="9" />
          <line x1="9" y1="15" x2="9" y2="21" />
          <line x1="15" y1="15" x2="15" y2="21" />
          <line x1="3" y1="9" x2="9" y2="9" />
          <line x1="3" y1="15" x2="9" y2="15" />
          <line x1="15" y1="9" x2="21" y2="9" />
          <line x1="15" y1="15" x2="21" y2="15" />
        </svg>
      );
    case 'learn':
      return (
        <svg {...p}>
          <path d="M12 6.5C10.6 5.3 8.6 4.5 6 4.5v13c2.6 0 4.6.8 6 2.2 1.4-1.4 3.4-2.2 6-2.2v-13c-2.6 0-4.6.8-6 2z" />
          <path d="M12 6.5v13.2" />
        </svg>
      );
    case 'chat':
      return (
        <svg {...p}>
          <path d="M21 12a8 8 0 0 1-8 8H7l-4 3v-4.5A8 8 0 1 1 21 12Z" />
          <path d="M8.5 11.5h7" />
          <path d="M8.5 14h4" />
        </svg>
      );
    case 'mine':
      return (
        <svg {...p}>
          <circle cx="12" cy="8" r="3.3" />
          <path d="M5.5 19.2c.9-3.3 3.5-5.2 6.5-5.2s5.6 1.9 6.5 5.2" />
        </svg>
      );
    case 'download':
      return (
        <svg {...p}>
          <path d="M12 4v10" />
          <path d="M8 11l4 4 4-4" />
          <path d="M5 19h14" />
        </svg>
      );
  }
}

export default function TabBar({ page, isApp = true }: { page: Page; isApp?: boolean }) {
  const active = tabOfPage(page);
  const tabs = isApp ? APP_TABS : WEB_TABS;
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white">
      <div className="mx-auto flex max-w-6xl">
        {tabs.map((t) => {
          const isActive = t.key === active;
          return (
            <a
              key={t.key}
              href={tabHome(t.key)}
              className={`flex flex-1 flex-col items-center gap-0.5 py-1.5 text-[10px] transition-colors ${
                isActive ? 'font-semibold text-slate-800' : 'text-slate-400 hover:text-slate-600'
              }`}
              style={{ paddingBottom: 'max(0.375rem, env(safe-area-inset-bottom))' }}
            >
              <span className={`flex h-7 w-14 items-center justify-center rounded-full ${isActive ? 'bg-slate-100' : ''}`}>
                <TabIcon name={t.key} />
              </span>
              {t.label}
            </a>
          );
        })}
      </div>
    </nav>
  );
}
